import React, { useMemo } from "react";
import { IconButton, Box, TextField, MenuItem } from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CloseIcon from "@mui/icons-material/Close";
import cn from "classnames";

import "./ShareItem.styles.scss";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { IUserInfo } from "../context/use-share-context-hook";
import { useShareContext } from "../context/share-context";
import { useFormatDateHook, useModals } from "hooks";
import { Avatar } from "../../../Avatar/Avatar";
import { leaveExternalRoles } from "utils/user";
import { IRole } from "store/types";

export interface ShareItemProps {
  roles: IRole[];
  onResend?: (id: number) => Promise<void>;
  isActive: boolean;
  share: IUserInfo;
}

export const ShareItem: React.FC<ShareItemProps> = ({ roles, onResend, isActive, share }) => {
  const formatDate = useFormatDateHook();
  const { deleteModal } = useModals();
  const { updateUserRole, deleteShare } = useShareContext();

  const minimumRoles = useMemo(() => {
    return leaveExternalRoles(roles);
  }, [roles]);

  const handleDelete = () => {
    deleteModal({
      action: async () => {
        if (!share.type) return;
        deleteShare(share.shareId, share.type);
      },
      message: `Are you sure you want to remove ${share.user.full_name} from the deal?`,
      element: "Remove access",
      onPressSuccess: "Remove",
      onPressCancel: "Cancel"
    });
  };

  return (
    <div className={cn("share-item__user-block", { "share-item__user-block__active": isActive })}>
      <div className="share-item__user-info">
        <div className="share-item__user-info__avatar">
          <Avatar
            {...(share.user.profile_photo_url ? { src: share.user.profile_photo_url } : null)}
            className="share-dialog__avatar"
          />
          <div className="share-item__user-data">
            <span className="share-item__user-name">{share.user.full_name}</span>
            <span className="share-dialog__user-email">{share.user.email}</span>
          </div>
        </div>
        <Box
          component="form"
          sx={{ "& .MuiTextField-root": { width: "100px", border: "none" } }}
          noValidate
          autoComplete="off"
          className="share-item__outlined-select"
        >
          <TextField
            id="share-item__outlined-select"
            select
            size="small"
            variant="standard"
            InputProps={{
              disableUnderline: true,
              style: { textAlign: "center" }
            }}
            value={share.role.id}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const selectedRole = parseInt(event.target.value, 10);
              updateUserRole(share.shareId, selectedRole);
            }}
          >
            {minimumRoles.map((role) => (
              <MenuItem className="share-item__outlined-select_options" key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {share?.last_seen ? (
          <Box display="flex" flexDirection="column" alignItems="center" fontSize="14px" color="#5d7782">
            <span>Last seen</span>
            <span>{formatDate({ date: share.last_seen })}</span>
          </Box>
        ) : (
          <Box color="#5d7782">
            <span>Waiting...</span>
          </Box>
        )}
      </div>
      <div className="share-item__actions">
        {share?.type === "invite" && (
          <IconButton onClick={() => onResend && onResend(share.shareId)}>
            <MaterialTooltip isShow title="Resend invite" placement="top">
              <EmailOutlinedIcon sx={{ color: "#0a3142" }} />
            </MaterialTooltip>
          </IconButton>
        )}
        <IconButton onClick={handleDelete}>
          <MaterialTooltip isShow title="Remove access" placement="top">
            <CloseIcon sx={{ color: "#0a3142", fontSize: "16px" }} viewBox="1 1 21 21" />
          </MaterialTooltip>
        </IconButton>
      </div>
    </div>
  );
};
